// Libraries
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { rem } from 'polished';

// Components
import MyTeamChip from '~/pages/profile/my-team/my-team-chip';
import DeleteButton from '~/pages/profile/my-team/my-team-remove-user';
import GracefulImage from '~/components/ui/layout/graceful-image';

const roundToOne = num => {
	return +(Math.round(num + 'e+1') + 'e-1');
};

/**********
 ** Component: TeamMemberDetailCard
 ** @type: functional stateless component
 ** @description: Photo of a team member
 **********/
const TeamMemberDetailCard = props => {
	const renderPhoto = (photo, name) => {
		if (photo) {
			return (
				<GracefulImage
					src={`${photo}`}
					width="138"
					height="138"
					alt={`This is ${name}`}
					placeholderColor="hsl(240, 9%, 98%)"
					noLazyLoad={true}
				/>
			);
		} else {
			return (
				<svg xmlns="http://www.w3.org/2000/svg" data-name="Layer 1" viewBox="0 0 138 138">
					<path
						fill="#fff"
						stroke="#b4bcc4"
						strokeDasharray="6 6"
						strokeWidth="2"
						d="M69 137A68 68 0 1 0 1 69a68 68 0 0 0 68 68z"
					/>
					<path
						fill="none"
						stroke="#b4bcc4"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2.5"
						d="M42.52 104.48a26.75 26.75 0 1 1 53.49 0M69.27 32.54A15.46 15.46 0 1 1 53.89 48a15.43 15.43 0 0 1 15.38-15.46z"
					/>
				</svg>
			);
		}
	};

	const renderDistance = distance => {
		if (distance >= 0) {
			return <h5 className="content__distance">{`${roundToOne(distance)}Km`}</h5>;
		} else {
			return null;
		}
	};

	const renderPosition = position => {
		if (position) {
			return (
				<div className="content__ranking">
					<h6 className="content__ranking__title">Challenge Ranking</h6>
					<p className="content__ranking__position">{`#${position}`}</p>
				</div>
			);
		} else {
			return null;
		}
	};

	const renderLastActivity = lastActivity => {
		if (lastActivity) {
			return (
				<div className="content__date">
					<h6 className="content__date__title">Last Activity </h6>
					<time className="content__date__timestamp">{`${lastActivity}`}</time>
				</div>
			);
		} else {
			return null;
		}
	};

	const renderJoinedDate = joinedDate => {
		if (joinedDate) {
			return (
				<div className="content__date">
					<h6 className="content__date__title">Joined </h6>
					<time className="content__date__timestamp">{`${joinedDate}`}</time>
				</div>
			);
		} else {
			return null;
		}
	};

	const renderDeleteButton = (isCaptain, userId, name, isCaptainSignedIn) => {
		if (isCaptain) {
			return <MyTeamChip isCaptain={isCaptain} isUser={props.isUser} isRequired={props.isRequired} />;
		} else {
			if (isCaptainSignedIn) {
				return <DeleteButton userId={userId} label={`Remove from team`} />;
			} else {
				return (
					<MyTeamChip
						isCaptain={props.isCaptain}
						isUser={props.isUser}
						isRequired={props.isRequired}
						onClick={props.onClick}
					/>
				);
			}
		}
	};

	return (
		<Wrapper className={`cell ${props.className}`}>
			<Center>
				<figure className="photo">{renderPhoto(props.photo, props.name)}</figure>
				<div className="content">
					{renderDistance(props.distance)}
					{renderDeleteButton(props.isCaptain, props.userId, props.name, props.isCaptainSignedIn)}
					<div className="content__meta">
						{renderPosition(props.ranking)}
						{renderLastActivity(props.lastActivity)}
						{renderJoinedDate(props.joinedDate)}
					</div>
				</div>
			</Center>
		</Wrapper>
	);
};

const Wrapper = styled.li`
  display: flex;
  flex-direction: column;
  width: ${rem('96px')};
  margin-right: 0;

  .photo {
    width: ${rem('96px')};
    height: ${rem('96px')};
    border-radius: ${rem('96px')};
    margin-bottom: ${rem('12px')};
    background-color: ${props => props.theme.gray0};
    display: table;

    img, svg {
      display: flex;
      width: ${rem('96px')};
      height: ${rem('96px')};
      vertical-align: middle;
      display: table-cell;
      text-align: center;
    }

    img {
      border-radius: ${rem('96px')};
      background-color: ${props => props.theme.gray0};

      object-fit: cover;
      object-position: center center;
      color: ${props => props.theme.gray3};
    }
  }

  @media ${props => props.theme.breakpointLarge}{
    width: ${rem('138px')};

    .photo {
      width: ${rem('138px')};
      height: ${rem('138px')};
      border-radius: ${rem('138px')};

      img, svg {
        width: ${rem('138px')};
        height: ${rem('138px')};
      }

      img {
        border-radius: ${rem('138px')};
      }
    }
  }


  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    &__distance {
      font-size: ${rem('32px')};
      color: ${props => props.theme.gray9}
      letter-spacing: 0;
      text-align: center;
      margin-bottom: ${rem('8px')};
      font-weight: 600;
      line-height: 1;
    }

		&__date,
		&__distance,
		&__meta,
		&__last-activity {
      width: 100%;
		}

		&__meta {
				margin-top: ${rem('24px')};
				margin-bottom: 0;
		}

		&__date,
		&__ranking {
			&, &:only-child{
				margin-bottom: 0;
			}

			&:nth-of-type(1){
				margin-bottom: ${rem('16px')};
			}
			&__title,
			&__timestamp,
			&__position {
				line-height: 1;
				margin-bottom: 0;

			}
			&__title {
				font-size: ${rem('12px')};
				text-transform: uppercase;
				color: ${props => props.theme.gray7}
				letter-spacing: 1px;
			}
			&__timestamp,
			&__position{
				color: ${props => props.theme.gray7}
			}

			&__timestamp {
				font-size: ${rem('14px')};
				text-transform: capitalize;
			}

			&__position {
				font-size: ${rem('24px')};
			}
		}
  }
`;

const Center = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: flex-start;
`;

TeamMemberDetailCard.defaultProps = {
	isCaptainSignedIn: false,
	className: `xlarge-2 large-3 small-6`,
};

TeamMemberDetailCard.propTypes = {
	isCaptainSignedIn: PropTypes.bool,
	photo: PropTypes.string,
	name: PropTypes.string,
	isCaptain: PropTypes.bool,
	isUser: PropTypes.bool,
	isRequired: PropTypes.bool,
	onClick: PropTypes.func,
	className: PropTypes.string,
};

export default TeamMemberDetailCard;



// WEBPACK FOOTER //
// ./src/components/general/modal/types/individual-detail/team-member-detail-card.jsx